import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Link } from '@abyss/web/ui/Link';
import { Popover } from '@abyss/web/ui/Popover';
import { Text } from '@abyss/web/ui/Text';
import { find } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Constants } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { EnhancedVirtualResultSection } from '../../../../common/ResultSection/EnhancedVirtualResultSection';
import {
  HeaderWrapper,
  HeadingContainer,
  LinkWrapper,
  ResultWrapper,
  headerTextStyle as defaultHeaderTextStyle,
  viewAllTextStyle,
} from '../../../../common/ResultSection/ResultSection.style';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useSnackcardViewAllCount } from '../../../../hooks/useSnackcardViewAllCount';
import { trimBrackets } from '../../../../utils/string.utils';

const VirtualCareProviderSection = ({
  virtualVisitViewAll,
  paddingBottom = '48px',
  testId = 'virtual-visit-heading',
  headerTextStyle = defaultHeaderTextStyle,
  virtualCareProviders,
  setIsVirtualCareListView,
}) => {
  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();
  const defaultSearchRadiusMessage = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.VIRTUAL_CARE_RESULT_SECTION_TOOLTIP,
  });

  const virtualCareProviderCount =
    virtualCareProviders &&
    Array.isArray(virtualCareProviders) &&
    virtualCareProviders.length > 0
      ? virtualCareProviders.reduce(
          (total, category) => total + (category?.providers?.length || 0),
          0
        )
      : 0;

  const { isExpandViewAllEnabled, viewAllCount, styleProps, css } =
    useSnackcardViewAllCount(virtualCareProviderCount);
  const [isOpenPopover, setIsOpenPopover] = useState(false);
  const { t } = useTranslation();

  return (
    <div style={{ paddingBottom }}>
      <HeaderWrapper>
        <ResultWrapper>
          <HeadingContainer>
            <Heading
              css={headerTextStyle}
              data-testid={testId}
              display="h6"
              offset={2}
            >
              <div
                data-testid={`${testId}-title`}
                style={{
                  display: 'flex',
                  gap: '2px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {virtualVisitViewAll}

                <Popover
                  aria-label="Virtual Care results Section"
                  content={defaultSearchRadiusMessage?.message}
                  css={{
                    '.abyss-popover-title': {
                      marginRight: '2px',
                    },
                    '.abyss-popover-trigger': {
                      display: 'flex',
                      position: 'relative',
                      top: '2px',
                    },
                  }}
                  data-auto-testid="result-section-popover-icon"
                  data-testid="result-section-popover-icon"
                  onOpenChange={() => setIsOpenPopover(!isOpenPopover)}
                  open={isOpenPopover}
                  title={defaultSearchRadiusMessage?.title}
                  width={370}
                >
                  <IconMaterial
                    data-auto-testid="result-section-popover-icon"
                    data-testid="result-section-popover-icon"
                    icon="info"
                    onClick={() => setIsOpenPopover(!isOpenPopover)}
                    size={16}
                    variant="outlined"
                  />
                </Popover>
              </div>
            </Heading>
          </HeadingContainer>
        </ResultWrapper>
        {virtualCareProviderCount > 3 ? (
          <LinkWrapper>
            <Link
              after={
                <IconMaterial
                  icon="chevron_right"
                  size={(isExpandViewAllEnabled && '$md') || '$xs'}
                />
              }
              aria-label={`${t('View all')} ${trimBrackets(viewAllCount)}`}
              css={css}
              data-auto-testid="view-all-virtual-care-link"
              data-testid="view-all-virtual-care-link"
              isStandardAnchor
              onClick={(e) => {
                e.preventDefault();
                const url = new URL(window.location.href);
                url.searchParams.set('virtualCareListViewPage', 'true');
                window.history.pushState({}, '', url.toString());
                setIsVirtualCareListView(true);
              }}
            >
              <Text
                color="$interactive1"
                {...styleProps}
                css={(!isExpandViewAllEnabled && viewAllTextStyle) || {}}
              >
                {`${t('View all')} ${viewAllCount}`}
              </Text>
            </Link>
          </LinkWrapper>
        ) : null}
      </HeaderWrapper>
      <EnhancedVirtualResultSection
        showCarouselInMobile={true}
        virtualCareProviderCount={virtualCareProviderCount}
        virtualCareProviders={virtualCareProviders}
      />
    </div>
  );
};

export default VirtualCareProviderSection;
